import { InjectionToken } from '@angular/core'
import { Observable } from 'rxjs'

/**
 * Dialog structure
 */

type DialogSize =
  | 'xxxs'
  | 'xxs'
  | 'xs'
  | 'sm'
  | 'md'
  | 'lg'
  | 'xl'
  | 'xxl'
  | 'xxxl'

type DialogIcon = 'info' | 'delete' | 'warning'

type DialogColor =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'info'
  | 'warning'
  | 'danger'
  | 'light'
  | 'dark'

export interface DialogParams {
  title?: string
  titleParams?: Array<string | number>
  message?: string
  messageParams?: Array<string | number>
  color?: DialogColor
  icon?: DialogIcon
  size?: DialogSize
}

export type DialogResponse = 'CONFIRM' | 'CANCEL'

/**
 * Notification base data
 */
export type NotificationLevel = 'success' | 'info' | 'error' | 'warning'
export type NotificationType = 'event' | 'alert' | 'notification'
export declare interface Notification {
  code: string
  type: NotificationType
  level?: NotificationLevel
  dialog?: DialogParams
}

/**
 * Notification service base structure
 */
export declare interface NotificationManager {
  show: (params: DialogParams, override?: any) => any
  show$: (params: DialogParams) => Observable<void>
  success: (params: DialogParams, override?: any) => any
  error: (params: DialogParams, override?: any) => any
  warning: (params: DialogParams, override?: any) => any
  info: (params: DialogParams, override?: any) => any
}

export const NOTIFICATION_MANAGER = new InjectionToken<NotificationManager>(
  'NotificationManager',
)

/**
 * Notification settings for toast notifications
 */
export type NotificationColor = 'success' | 'warn' | 'info' | 'error'

export interface NotificationOptions {
  title?: string
  message?: string
  color?: NotificationColor
  disableTimeout?: boolean
  disableHide?: boolean
  bigger?: boolean
  onClick?: () => void
}

export type GenericNotificationCode =
  | 'ATTACHMENT_DELETED'
  | 'ATTACHMENT_UPLOADED'
  | 'CHANGES_NOT_APPLIED'
  | 'EXPORT_REQUESTED'
  | 'FILE_NOT_FOUND'
  | 'FILE_UPLOADED'
  | 'FORM_INCOMPLETE'
  | 'ORDER_PACKED'
  | 'INSERT_SKU_OR_BARCODE'
  | 'PICKING_LIST_NOT_FOUND'
  | 'PRINT_SENT'
  | 'PRINT_IN_PROGRESS'
  | 'PRINTERS_NOT_FOUND'
  | 'PRINTERS_NOT_SELECTED'
  | 'QTY_LIMIT'
  | 'REF_COPIED'
  | 'ROW_NOT_FOUND'
  | 'SKU_NOT_VALID'
  | 'TENANT_NOT_FOUND'
  | 'UPDATE_COMPLETED'
  | 'UPDATE_ERRORED'
  | 'WAREHOUSE_NOT_FOUND'
  | 'WRONG_SIZES'

export function parseGenericNotification(
  code?: GenericNotificationCode,
): NotificationOptions {
  switch (code) {
    case 'ATTACHMENT_DELETED':
      return {
        message: 'Attachment deleted successfully',
        color: 'success',
      }
    case 'ATTACHMENT_UPLOADED':
      return {
        message: 'Attachment uploaded successfully',
        color: 'success',
      }
    case 'CHANGES_NOT_APPLIED':
      return {
        message: 'Changes not applied. Please try again',
        color: 'error',
      }
    case 'EXPORT_REQUESTED':
      return {
        message:
          'Export requested successfully. You will receive it via email soon',
        color: 'info',
      }
    case 'FILE_NOT_FOUND':
      return {
        message: 'Attachment not found',
        color: 'error',
      }
    case 'FILE_UPLOADED':
      return {
        title: 'Import in progress',
        message:
          'Requested updates will be available soon and you will receive a summary email',
        color: 'info',
      }
    case 'FORM_INCOMPLETE':
      return {
        title: 'Attention',
        message: 'Fill out the form with all the necessary data',
        color: 'error',
      }
    case 'ORDER_PACKED':
      return {
        message: 'Order packed successfully',
        color: 'success',
      }
    case 'INSERT_SKU_OR_BARCODE':
      return {
        message: 'Insert either the SKU or the barcode of the product',
        color: 'error',
      }
    case 'PICKING_LIST_NOT_FOUND':
      return {
        message: 'Picking list not found',
        color: 'error',
      }
    case 'PRINT_SENT':
      return {
        message: 'Print sent successfully',
        color: 'success',
      }
    case 'PRINT_IN_PROGRESS':
      return {
        message: 'Print in progress',
        color: 'info',
      }
    case 'PRINTERS_NOT_FOUND':
      return { message: 'Printers not found', color: 'error' }
    case 'PRINTERS_NOT_SELECTED':
      return { message: 'Printers not selected', color: 'error' }
    case 'QTY_LIMIT':
      return {
        message: 'Quantity limit reached. Reduce the input quantity',
        color: 'error',
      }
    case 'REF_COPIED':
      return {
        message: 'Reference copied successfully',
        color: 'success',
      }
    case 'ROW_NOT_FOUND':
      return {
        message: 'Row not found',
        color: 'error',
      }
    case 'SKU_NOT_VALID':
      return { message: 'SKU not valid', color: 'error' }
    case 'TENANT_NOT_FOUND':
      return {
        message: 'Tenant not found',
        color: 'error',
      }
    case 'UPDATE_COMPLETED':
      return {
        message: 'Update completed',
        color: 'info',
      }
    case 'UPDATE_ERRORED':
      return {
        message: 'An error occurred',
        color: 'error',
      }
    case 'WAREHOUSE_NOT_FOUND':
      return {
        message: 'Warehouse not found',
        color: 'error',
      }
    case 'WRONG_SIZES':
      return {
        message: 'One or more sizes have the wrong value',
        color: 'error',
      }
    default:
      return {}
  }
}

/**
 * NgNotificationService interface for the new notification system
 */
export abstract class NgNotificationServiceInterface {
  abstract show(options: NotificationOptions): void
}
